import { Button, Typography, useTheme } from '@mui/material';
import { ReactNode } from 'react';
import { useBoolean } from 'usehooks-ts';

import { DocumentScanner } from '@/components/DocumentScanner';
import { MobileOnly } from '@/components/Layout/Responsive';

import { useTranslate } from '@/hooks/useTranslate';

import { FileSelector } from './FileSelector';

import { DocumentScannerOverlay } from './components.styled';

type DocumentSelectorProps = {
  title?: string;
  description?: ReactNode;
  isOptional?: boolean;
  onSetFile: (value: File | string) => void;
  onBack?: () => void;
};

export function DocumentSelector({
  title,
  description,
  isOptional,
  onSetFile,
  onBack,
}: DocumentSelectorProps) {
  const { t } = useTranslate();
  const theme = useTheme();
  const {
    value: shouldShowCamera,
    setFalse: hideCamera,
    setTrue: showCamera,
  } = useBoolean(false);

  const handleDocumentCapture = (imageDataUrl: string) => {
    onSetFile(imageDataUrl);
    hideCamera();
  };

  return (
    <>
      {isOptional && (
        <Typography
          variant="body1"
          color={theme.palette.grey[700]}
          textAlign="center"
          mb={1}
        >
          {t('moreInfo.documentSelector.optional')}
        </Typography>
      )}
      <Typography variant="h3" mb={1} textAlign="center">
        {title}
      </Typography>
      {description &&
        (typeof description === 'string' ? (
          <Typography
            variant="body2"
            color={theme.palette.grey[800]}
            textAlign="center"
          >
            {description}
          </Typography>
        ) : (
          description
        ))}
      <FileSelector my={3} onSetFile={onSetFile} />
      <MobileOnly alignSelf="stretch">
        {isOptional ? (
          <Button variant="outlined" fullWidth onClick={onBack}>
            {t('moreInfo.documentSelector.button.remindMe')}
          </Button>
        ) : (
          <Button variant="outlined" fullWidth onClick={showCamera}>
            {t('moreInfo.documentSelector.button.takeAPhoto')}
          </Button>
        )}
        {shouldShowCamera && (
          <DocumentScannerOverlay>
            <DocumentScanner
              autoCapture={true}
              onBack={hideCamera}
              onConfirm={handleDocumentCapture}
            />
          </DocumentScannerOverlay>
        )}
      </MobileOnly>
    </>
  );
}
